<template>
  <div data-cy="inventory-usage-row" class="flex items-center gap-2 py-1">
    <div class="flex-1 min-w-0">
      <InventoryPartSearch
        :model-value="partSelection"
        @select="onPartSelect"
        @clear="onPartClear"
      />
    </div>
    <div class="w-44 shrink-0">
      <SelectInput
        :model-value="usage.inventory_location_id"
        :options="locationOptions"
        :filter="true"
        placeholder="Location"
        data-cy="inventory-location-select"
        :body-render="false"
        @update:model-value="onLocationChange"
      />
    </div>
    <div class="w-16 shrink-0">
      <input
        data-cy="inventory-quantity-input"
        type="number"
        :value="usage.quantity"
        min="1"
        class="w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 p-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        @input="onQuantityChange"
      />
    </div>
    <button
      data-cy="inventory-delete-btn"
      type="button"
      class="shrink-0 p-2 text-gray-400 hover:text-red-500"
      @click="emit('remove')"
    >
      <svg
        class="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

import SelectInput from "@/components/Seranova/SelectInput/SelectInput.vue";
import type { InventoryLocation } from "@/typings/inventory";
import InventoryPartSearch from "./InventoryPartSearch.vue";

export interface EditableUsage {
  part_id: number | null;
  part_name: string;
  inventory_location_id: number | null;
  quantity: number;
}

const props = defineProps<{
  usage: EditableUsage;
  locations: InventoryLocation[];
}>();

const emit = defineEmits<{
  "update:usage": [usage: EditableUsage];
  remove: [];
}>();

const locationOptions = computed(() =>
  props.locations.map((l) => ({ label: l.name, value: l.id }))
);

const partSelection = computed(() =>
  props.usage.part_id
    ? { id: props.usage.part_id, name: props.usage.part_name }
    : null
);

const onPartSelect = (part: { id: number; name: string }) => {
  emit("update:usage", {
    ...props.usage,
    part_id: part.id,
    part_name: part.name
  });
};

const onPartClear = () => {
  emit("update:usage", {
    ...props.usage,
    part_id: null,
    part_name: ""
  });
};

const onLocationChange = (val: number | null) => {
  emit("update:usage", {
    ...props.usage,
    inventory_location_id: val
  });
};

const onQuantityChange = (e: Event) => {
  const val = parseInt((e.target as HTMLInputElement).value, 10);
  if (isNaN(val) || val <= 0) return;
  emit("update:usage", {
    ...props.usage,
    quantity: val
  });
};
</script>
